import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FaqList } from '@/components/generals/faq-list'
import { StatGrid } from '@/components/generals/stat-grid'
import { QuoteTabs } from '@/components/generals/quote-tabs'
import { GuideLinks } from '@/components/generals/guide-links'
import { ClosingCta } from '@/components/generals/closing-cta'
import { SwitchBand } from '@/components/generals/switch-band'
import { ReasonTriad } from '@/components/generals/reason-triad'
import { ProductHero } from '@/components/generals/product-hero'
import { GettingStarted } from '@/components/generals/getting-started'
import { HmsHeroVisual } from '@/components/landing/hms/hero-visual'
import { BinderScene } from '@/components/landing/hms/binder-scene'
import { VOICES } from '@/constants/testimonials'

const FAQ: Faq[] = [
    {
        question: 'Oppfyller HMS-systemet kravene ved tilsyn?',
        answer: [
            "Ja. Systemet er bygget etter kravene fra Arbeidstilsynet, med ferdige maler og egen hjelp når tilsynet kommer."
        ]
    },
    {
        question: 'Hva betyr «vi setter opp alt for deg»?',
        answer: [
            "Etter en kort samtale lager ekspertene våre alle dokumentene bedriften trenger, og laster dem opp i Svenn for deg. Vil du heller prøve selv først, kan du det."
        ]
    },
    {
        question: 'Er det vanskelig å bruke?',
        answer: [
            "Nei. Det tar et par trykk, og ingen trenger kurs eller opplæring for å komme i gang."
        ]
    },
    {
        question: 'Fungerer appen uten dekning?',
        answer: [
            "Ja. Sjekklister, SJA og avvik lagres på mobilen og synkroniseres automatisk når du er tilbake på nett."
        ]
    },
    {
        question: 'Hvordan fungerer prøveperioden?',
        answer: [
            "Du trenger ikke kredittkort. Alle funksjonene er tilgjengelige, du kan invitere de ansatte, og du får hjelp fra en digital assistent og support på chat, e-post og telefon."
        ]
    },
    {
        question: 'Hva koster HMS-systemet?',
        answer: [
            "Smart-pakken koster fra 519 kr i måneden med én bruker inkludert. Total-pakken koster fra 839 kr i måneden, og inkluderer tilsynshjelp, ressursplanlegger og sentral godkjenning. Alle priser er eks. mva."
        ],
        link: { label: 'Se full prisoversikt', href: '/priser' }
    }
]

const Hms: FC = (): ReactNode => {
    return (
        <Layout header='dark'>
            <Seo
                title='HMS-system'
                description="HMS-systemet som alltid er klart for tilsyn. Vi setter opp håndbok, rutiner og skjemaer for deg, og hjelper deg når tilsynet kommer. Prøv gratis i 14 dager."
            />

            <ProductHero
                tone='deep'
                eyebrow='HMS-system · 400+ tilsyn bestått uten anmerkninger'
                titleA='Alltid klar'
                titleB='for tilsyn.'
                text='Vi setter opp HMS-dokumentasjonen for deg og stiller opp når tilsynet kommer. Sjekklister, SJA og avvik fyller dere ut på sekunder, rett på byggeplassen.'
                visual={<HmsHeroVisual />}
                points={['Krever ikke kredittkort', 'Komplett oppsett gjort for deg', 'Hjelp ved tilsyn']}
                quote={{
                    quote: 'Med hjelp av Svenn fikk vi opp et fullstendig HMS-system samme dag vi tok kontakt.',
                    name: 'Bjørn Erik',
                    company: 'NBT bygg AS'
                }}
            />

            <ReasonTriad
                index='01'
                eyebrow='Derfor velger håndverkere HMS fra Svenn'
                title='Et HMS-system som ikke blir brukt, dekker ingen krav.'
                intro='Derfor gjør vi oppsettet for deg, og lager en app hele laget faktisk bruker.'
                reasons={[
                    { title: 'Vi setter opp alt for deg', text: 'Ekspertene våre lager håndbok, rutiner og skjemaer tilpasset bedriften din.' },
                    { title: 'En app hele laget bruker', text: 'Ingen kurs og ingen manual. Et par trykk, så er sjekklisten eller avviket registrert.' },
                    { title: 'Årlig revisjon og hjelp ved tilsyn', text: 'Vi går gjennom systemet hvert år. Kundene våre har bestått over 400 tilsyn uten anmerkninger.' }
                ]}
            />

            <BinderScene />

            <StatGrid
                index='03'
                tone='night'
                eyebrow='Tilsynet kommer'
                title='Tilsyn er ikke et spørsmål om hvis. Men når.'
                intro='Bygg og anlegg er bransjen Arbeidstilsynet besøker oftest, og den som oftest får gebyr.'
                stats={[
                    {
                        label: 'Av alle tilsyn',
                        value: '1 av 3',
                        text: 'tilsyn fra Arbeidstilsynet i 2024 var i bygg og anlegg. Totalt ble det gjennomført 4 000 tilsyn.',
                        note: 'Arbeidstilsynet, 2024.'
                    },
                    {
                        label: 'Av overtredelsesgebyrene',
                        value: '7 av 10',
                        text: 'overtredelsesgebyr går til byggebedrifter. Gebyret kan bli opptil 4 % av omsetningen, eller maks 50 G.',
                        note: 'Maks 50 G er rundt 6,2 millioner kroner fra 1. juli 2024.'
                    },
                    {
                        label: 'Bestått',
                        value: '400+',
                        text: 'tilsyn bestått uten anmerkninger av Svenns kunder.',
                        note: 'Svenns kunder per 2026.'
                    }
                ]}
            />

            <QuoteTabs
                index='04'
                tone='paper'
                title='HMS-bekymringene er borte for godt.'
                intro='Dette sier håndverkerne som har latt Svenn bære HMS-sekken.'
                items={[VOICES.bjornErik, VOICES.sigve, VOICES.thorstein]}
            />

            <SwitchBand
                index='05'
                title='Ingen grunn til å vegre seg for HMS'
                text='Du får fri bruk til oppsigelsestiden hos dagens leverandør er ute, så du slipper å betale dobbelt. Vi setter opp alt, og hjelper deg hvis tilsynet kommer.'
                points={['Komplett oppsett gjort for deg', 'Fri bruk ut oppsigelsestiden', 'Tilsynshjelp og årlig revisjon', 'Support alltid inkludert']}
                from='Papirpermer'
            />

            <GettingStarted
                index='06'
                tone='mist'
                intro='Fra første samtale til et HMS-system som er klart for tilsyn.'
                steps={[
                    { when: '2 minutter', title: 'Opprett konto', text: 'Ingen kort og ingen møter. Du er inne på to minutter.' },
                    { when: 'En kort samtale', title: 'Vi setter opp HMS-systemet', text: 'Etter en kort prat lager vi dokumentene, tilpasset bedriften din.' },
                    { when: 'Fra dag 1', title: 'Klar for tilsyn', text: 'Laget bruker appen, og HMS-teamet vårt står klart når du trenger det.' }
                ]}
            />

            <FaqList items={FAQ} index='07' />

            <ClosingCta
                tone='signal'
                eyebrow='HMS, ordnet'
                lines={['Slutt å bekymre deg', 'for HMS.']}
                text='Prøv Svenn gratis i 14 dager. Krever ikke kredittkort, og er 100 % uforpliktende.'
                note='Fra 519 kr i måneden, eller 839 kr i måneden med tilsynshjelp. Pris eks. mva.'
            />

            <GuideLinks
                index='08'
                title='Lær mer om HMS'
                intro='Gratis guider og maler fra bloggen.'
                guides={[
                    { kind: 'Guide og gratis mal', title: 'HMS-håndbok: 8 ting den må inneholde', href: '/blogg/guide-til-hms-handbok' },
                    { kind: 'Guide', title: 'Tilsyn på byggeplassen: slik forbereder du deg', href: '/blogg/tilsyn-pa-byggeplassen' },
                    { kind: 'Guide og gratis mal', title: 'SHA-plan: krav, ansvar og gratis mal', href: '/blogg/sha-plan' }
                ]}
            />
        </Layout>
    )
}

export default Hms
